import React, { Component, ComponentType } from 'react'
import { MenuList, MenuItem, Typography, styled } from '@mui/material'
import { IFilterConfigs } from './types'
import { FilterNotes } from './ui.units'
import { createFilterStore } from './filter-store'
import { filterMenuClasses, getFieldsMatches } from './helpers'
import { IDataViewContext, mapDataViewContext } from './context'

export interface ISearchMatchProps {}

function createSearchMatch<T>(): ComponentType<ISearchMatchProps> {
  class SearchMatch extends Component<ISearchMatchProps> {
    render = () => {
      return mapDataViewContext<T>((context) => {
        const { keyword } = context
        const { fields, quickSearch } = context.filterBarConfigs
        const fieldMatches = getFieldsMatches<T>(fields, keyword)
        const keys = Object.keys(fieldMatches).filter((x) => !!x) as (keyof IFilterConfigs<T>)[]
        const quickSearchLabel = quickSearch?.label ?? 'Quick search'

        return (
          <MenuListCustom className={filterMenuClasses.list}>
            {keys.map((key, i) => {
              const item = fieldMatches[key]
              const label = item?.label ?? key.toString()
              const notes = item?.quickSearchMatch?.notes
              return (
                <MenuItem key={key.toString() + i} className={filterMenuClasses.item} onClick={() => this.handleClick(context, key)}>
                  <Typography noWrap variant='body2' component='span' sx={{ flex: 1 }}>
                    {label} "{keyword}"
                  </Typography>
                  {notes && <FilterNotes title={`The ${label} includes`} disabledSize {...notes} />}
                </MenuItem>
              )
            })}
            {quickSearch && (
              <MenuItem className={filterMenuClasses.item} onClick={() => this.handleQuickSearchClick(context)}>
                <Typography noWrap variant='body2' component='span' sx={{ flex: 1 }}>
                  {quickSearchLabel ?? 'Quick search'} &quot;{keyword}&quot;
                </Typography>
                {quickSearch.notes && (
                  <FilterNotes sx={{ ml: 1 }} title={`The ${quickSearchLabel.toLocaleLowerCase()} includes`} disabledSize {...quickSearch.notes} />
                )}
              </MenuItem>
            )}
          </MenuListCustom>
        )
      })
    }

    handleClick = (context: IDataViewContext<T>, field: keyof T) => {
      const keyword = context.keyword?.trim()
      if (!keyword) return
      const store = createFilterStore(context.filterState)
      store.addFilterItem(field, { value: keyword }, { reason: 'quickSearch', area: 'input' })
      context.onFilterStateChange?.(store.build())
    }

    handleQuickSearchClick = (context: IDataViewContext<T>) => {
      const keyword = context.keyword?.trim()
      if (!keyword) return
      const store = createFilterStore(context.filterState)
      store.addQuickSearch(keyword, { reason: 'quickSearch', area: 'input' })
      context.onFilterStateChange?.(store.build())
    }
  }
  return SearchMatch
}
export default createSearchMatch

const MenuListCustom = styled(MenuList)({
  padding: 0,
  [`.${filterMenuClasses.item}`]: {
    height: 'var(--filter-item-height, 40px)',
    padding: '8px 16px',
    '&:not(:last-child)': {
      borderBottom: '1px solid var(--color-divider, rgba(145 160 170 / 10%))'
    }
  }
})
